import { useState, useMemo } from "react";
import {
  useV1ProductionProductionLinesList,
  useV1ProductionProductionLinesCreate,
  useV1ProductionWorkCentersList,
  useV1ProductionWorkCentersCreate,
  useV1ProductionWorkCentersPartialUpdate,
  useV1ProductionMachinesList,
  useV1ProductionMachinesCreate,
} from "@/api/generated/v1/v1";
import { useProductionLineUIStore } from "./store";
import { PageBanner } from "@/shared/components/PageBanner";

/**
 * Production Line & Machine management screen (ADR-100).
 * Standardized to strictly match DashboardPage aesthetics, brand design tokens,
 * and provide complete hierarchical tree & pipeline flow visualization.
 */
export default function ProductionLinePage() {
  // Queries & Mutations
  const { data: linesData, isLoading: linesLoading, refetch: refetchLines } =
    useV1ProductionProductionLinesList({ page_size: 1000 } as any);
  const createLine = useV1ProductionProductionLinesCreate();

  const { selectedLineId, selectedWorkCenterId, setSelectedLine, setSelectedWorkCenter } =
    useProductionLineUIStore();

  // Fetch all work centers once so counts, unassigned stations and cross-line navigation remain stable
  const { data: workCentersData, isLoading: workCentersLoading, refetch: refetchWorkCenters } =
    useV1ProductionWorkCentersList({ page_size: 1000 } as any);
  const assignWorkCenter = useV1ProductionWorkCentersPartialUpdate();

  const { data: machinesData, refetch: refetchMachines } = useV1ProductionMachinesList(
    selectedWorkCenterId ? { work_center: selectedWorkCenterId } : {},
    { query: { enabled: !!selectedWorkCenterId } }
  );
  const createMachine = useV1ProductionMachinesCreate();
  const createWorkCenter = useV1ProductionWorkCentersCreate();

  // Local UI states
  const [viewMode, setViewMode] = useState<"flow" | "tree">("flow");
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateLineOpen, setIsCreateLineOpen] = useState(false);
  const [isCreateMachineOpen, setIsCreateMachineOpen] = useState(false);
  const [isCreateWcOpen, setIsCreateWcOpen] = useState(false);
  const [targetWcForMachine, setTargetWcForMachine] = useState<string | null>(null);
  const [targetLineForWc, setTargetLineForWc] = useState<string | null>(null);

  // Tree expansion state (default: all expanded for immediate visibility)
  const [collapsedLines, setCollapsedLines] = useState<Record<string, boolean>>({});
  const [collapsedStations, setCollapsedStations] = useState<Record<string, boolean>>({});

  // Form states
  const [newLineCode, setNewLineCode] = useState("");
  const [newLineName, setNewLineName] = useState("");
  const [newMachineCode, setNewMachineCode] = useState("");
  const [newMachineName, setNewMachineName] = useState("");
  const [newWcCode, setNewWcCode] = useState("");
  const [newWcName, setNewWcName] = useState("");

  const lines = linesData?.results || [];
  const workCenters = workCentersData?.results || [];
  const machines = machinesData?.results || [];

  // Group work centers by production line
  const groupedWorkCenters = useMemo(() => {
    const map: Record<string, typeof workCenters> = {};
    workCenters.forEach((wc) => {
      const lineId = (wc.production_line_display as any)?.id || wc.production_line || "unassigned";
      if (!map[lineId]) map[lineId] = [];
      map[lineId]!.push(wc);
    });
    // Sort work centers within each line by sequence_no
    Object.keys(map).forEach((key) => {
      map[key]?.sort((a, b) => (a.sequence_no ?? 99) - (b.sequence_no ?? 99));
    });
    return map;
  }, [workCenters]);

  // Statistics
  const totalLinesCount = lines.length;
  const totalWorkCentersCount = workCenters.length;
  const unassignedWcCount = (groupedWorkCenters["unassigned"] || []).length;
  const totalMachinesCount = useMemo(() => {
    return workCenters.reduce((acc, wc) => acc + (wc.machines?.length || 0), 0);
  }, [workCenters]);

  const toggleLineCollapse = (lineId: string) => {
    setCollapsedLines((prev) => ({ ...prev, [lineId]: !prev[lineId] }));
  };

  const toggleStationCollapse = (wcId: string) => {
    setCollapsedStations((prev) => ({ ...prev, [wcId]: !prev[wcId] }));
  };

  const expandAll = () => {
    setCollapsedLines({});
    setCollapsedStations({});
  };

  const collapseAll = () => {
    const linesMap: Record<string, boolean> = {};
    lines.forEach((l) => {
      linesMap[l.id] = true;
    });
    const stationsMap: Record<string, boolean> = {};
    workCenters.forEach((wc) => {
      stationsMap[wc.id] = true;
    });
    setCollapsedLines(linesMap);
    setCollapsedStations(stationsMap);
  };

  const handleCreateLine = () => {
    if (!newLineCode.trim() || !newLineName.trim()) return;
    createLine.mutate(
      { data: { code: newLineCode.trim(), name: newLineName.trim(), description: "", is_active: true } as any },
      {
        onSuccess: () => {
          setNewLineCode("");
          setNewLineName("");
          setIsCreateLineOpen(false);
          refetchLines();
        },
      }
    );
  };

  const handleCreateWorkCenter = () => {
    if (!newWcCode.trim() || !newWcName.trim()) return;
    createWorkCenter.mutate(
      {
        data: {
          code: newWcCode.trim(),
          name: newWcName.trim(),
          production_line: targetLineForWc || undefined,
          is_active: true,
        } as any,
      },
      {
        onSuccess: () => {
          setNewWcCode("");
          setNewWcName("");
          setIsCreateWcOpen(false);
          setTargetLineForWc(null);
          refetchWorkCenters();
        },
      }
    );
  };

  const handleCreateMachine = () => {
    const wcId = targetWcForMachine || selectedWorkCenterId;
    if (!wcId || !newMachineCode.trim() || !newMachineName.trim()) return;
    createMachine.mutate(
      {
        data: {
          work_center: wcId,
          code: newMachineCode.trim(),
          name: newMachineName.trim(),
          is_active: true,
        } as any,
      },
      {
        onSuccess: () => {
          setNewMachineCode("");
          setNewMachineName("");
          setIsCreateMachineOpen(false);
          setTargetWcForMachine(null);
          refetchWorkCenters();
          refetchMachines();
        },
      }
    );
  };

  // Filtered lines list
  const filteredLines = useMemo(() => {
    let result = lines;
    if (selectedLineId) {
      result = result.filter((l) => l.id === selectedLineId);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (l) => l.name.toLowerCase().includes(term) || l.code.toLowerCase().includes(term)
      );
    }
    return result;
  }, [lines, selectedLineId, searchTerm]);

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <style>{`
        .kpi-card {
          padding: 0.5rem 0.5rem 0.5rem 4rem;
          min-height: 110px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 16px;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 0.1rem;
          position: relative;
          overflow: hidden;
        }
        .kpi-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.15);
          border-color: rgba(0, 114, 112, 0.3);
        }
        .tree-line-branch {
          position: relative;
          border-right: 2px solid #007270;
          margin-right: 1.25rem;
          padding-right: 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .tree-station-branch {
          position: relative;
          border-right: 2px dashed #D4AF37;
          margin-right: 1.25rem;
          padding-right: 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
      `}</style>

      {/* 1. STANDARDIZED PAGE BANNER (DASHBOARD HEADER MATCH) */}
      <PageBanner
        title="نمای چارت و گرافیکی خطوط تولید"
        subtitle="زنجیرهٔ اتصالات ایستگاه‌های کاری، خطوط مونتاژ و نگاشت تجهیزات تولید"
        icon="🏭"
        helpLink="/help/production/lines"
      >
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          <button
            onClick={() => setIsCreateLineOpen(true)}
            style={{
              padding: "0.6rem 1.2rem",
              borderRadius: "12px",
              background: "#ffffff",
              color: "#007270",
              fontWeight: "700",
              fontSize: "0.9rem",
              border: "1px solid rgba(255, 255, 255, 0.8)",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>➕</span>
            <span>افزودن خط تولید</span>
          </button>

          <button
            onClick={() => {
              setTargetLineForWc(selectedLineId);
              setIsCreateWcOpen(true);
            }}
            style={{
              padding: "0.6rem 1.2rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: "700",
              fontSize: "0.9rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>🚉</span>
            <span>افزودن ایستگاه کاری</span>
          </button>

          <button
            onClick={() => {
              setTargetWcForMachine(selectedWorkCenterId);
              setIsCreateMachineOpen(true);
            }}
            style={{
              padding: "0.6rem 1.2rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: "700",
              fontSize: "0.9rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>⚙️</span>
            <span>افزودن دستگاه</span>
          </button>
        </div>
      </PageBanner>

      {/* 2. STATS GRID (MATCHING DASHBOARD KPI CARDS) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.5rem",
        }}
      >
        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(0,114,112,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            🏭
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              خطوط تولید فعال
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#007270" }}>
                {linesLoading ? "..." : totalLinesCount}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#10B981" }}>زنده</span>
            </div>
          </div>
        </div>

        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(245,158,11,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            🚉
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              ایستگاه‌های کاری
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#D4AF37" }}>
                {workCentersLoading ? "..." : totalWorkCentersCount}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#F59E0B" }}>ثبت شده</span>
            </div>
          </div>
        </div>

        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(59,130,246,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            ⚙️
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              کل ماشین‌آلات
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: "#3B82F6" }}>
                {totalMachinesCount}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#3B82F6" }}>مستقر</span>
            </div>
          </div>
        </div>

        <div className="brand-card kpi-card">
          <span style={{ fontSize: "1.8rem", background: "rgba(239,68,68,0.08)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            ⚠️
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1 }}>
            <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "var(--color-text-main, #2B2B2B)" }}>
              تخصیص‌نیافته
            </span>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "1.2rem", fontWeight: "800", color: unassignedWcCount > 0 ? "#EF4444" : "#10B981" }}>
                {unassignedWcCount}
              </span>
              <span style={{ fontSize: "0.8rem", fontWeight: "600", color: unassignedWcCount > 0 ? "#EF4444" : "#10B981" }}>
                {unassignedWcCount > 0 ? "نیازمند بررسی" : "عالی"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 3. CONTROL BAR & FILTER TABS (BRAND-CARD-EXTAND) */}
      <div
        className="brand-card-extand"
        style={{
          padding: "1rem 1.25rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        {/* View Mode Switcher Buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: "rgba(0,0,0,0.04)", padding: "4px", borderRadius: "12px" }}>
          <button
            onClick={() => setViewMode("flow")}
            style={{
              padding: "6px 14px",
              borderRadius: "8px",
              fontSize: "0.85rem",
              fontWeight: 700,
              border: "none",
              cursor: "pointer",
              background: viewMode === "flow" ? "#007270" : "transparent",
              color: viewMode === "flow" ? "#FFFFFF" : "var(--color-text-main, #2B2B2B)",
              boxShadow: viewMode === "flow" ? "0 2px 8px rgba(0,114,112,0.25)" : "none",
              transition: "all 0.2s",
            }}
          >
            📊 نمای چارتی خطوط (Pipeline Diagram)
          </button>
          <button
            onClick={() => setViewMode("tree")}
            style={{
              padding: "6px 14px",
              borderRadius: "8px",
              fontSize: "0.85rem",
              fontWeight: 700,
              border: "none",
              cursor: "pointer",
              background: viewMode === "tree" ? "#007270" : "transparent",
              color: viewMode === "tree" ? "#FFFFFF" : "var(--color-text-main, #2B2B2B)",
              boxShadow: viewMode === "tree" ? "0 2px 8px rgba(0,114,112,0.25)" : "none",
              transition: "all 0.2s",
            }}
          >
            🌳 نمای درختی سلسله‌مراتبی (Tree Network)
          </button>
        </div>

        {/* Tree Expand/Collapse Global Buttons (Visible in Tree View) */}
        {viewMode === "tree" && (
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <button
              onClick={expandAll}
              style={{
                padding: "5px 10px",
                borderRadius: "6px",
                fontSize: "0.8rem",
                fontWeight: 600,
                border: "1px solid var(--color-border, #E4DCC8)",
                background: "var(--color-surface, #F5F1E6)",
                cursor: "pointer",
              }}
            >
              ➕ گسترش همه
            </button>
            <button
              onClick={collapseAll}
              style={{
                padding: "5px 10px",
                borderRadius: "6px",
                fontSize: "0.8rem",
                fontWeight: 600,
                border: "1px solid var(--color-border, #E4DCC8)",
                background: "var(--color-surface, #F5F1E6)",
                cursor: "pointer",
              }}
            >
              ➖ بستن همه
            </button>
          </div>
        )}

        {/* Line Filter Tabs */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            onClick={() => setSelectedLine(null)}
            style={{
              padding: "6px 12px",
              borderRadius: "8px",
              fontSize: "0.85rem",
              fontWeight: 700,
              border: "1px solid var(--color-border, #E4DCC8)",
              background: selectedLineId === null ? "#007270" : "var(--color-surface, #F5F1E6)",
              color: selectedLineId === null ? "#FFFFFF" : "var(--color-text-main, #2B2B2B)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            همه خطوط ({totalLinesCount})
          </button>
          {lines.map((l) => (
            <button
              key={l.id}
              onClick={() => setSelectedLine(l.id)}
              style={{
                padding: "6px 12px",
                borderRadius: "8px",
                fontSize: "0.85rem",
                fontWeight: 700,
                border: "1px solid var(--color-border, #E4DCC8)",
                background: selectedLineId === l.id ? "#007270" : "var(--color-surface, #F5F1E6)",
                color: selectedLineId === l.id ? "#FFFFFF" : "var(--color-text-main, #2B2B2B)",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              🏭 {l.name}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <div style={{ position: "relative", minWidth: "220px" }}>
          <input
            type="text"
            placeholder="جستجوی نام یا کد خط..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "10px",
              border: "1px solid var(--color-border, #E4DCC8)",
              fontSize: "0.85rem",
              background: "#FFFFFF",
              color: "#2B2B2B",
              outline: "none",
            }}
          />
        </div>
      </div>

      {/* 4. MAIN GRAPHICAL CANVAS */}
      <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        {linesLoading ? (
          <div style={{ textAlign: "center", padding: "3rem", color: "#64748B" }}>
            <div style={{ fontSize: "1.8rem" }}>🔄</div>
            <p style={{ marginTop: "0.5rem", fontWeight: 600 }}>در حال بارگذاری اطلاعات ساختاری خطوط تولید...</p>
          </div>
        ) : filteredLines.length === 0 ? (
          <div className="brand-card-extand" style={{ textAlign: "center", padding: "3rem" }}>
            <p style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)" }}>هیچ خط تولیدی یافت نشد.</p>
          </div>
        ) : viewMode === "tree" ? (
          /* =========================================================================
             TRUE HIERARCHICAL TREE NETWORK VIEW (ADR-100)
             Line (Root) ──► Station (Level 1) ──► Machine (Level 2 Leaf)
             ========================================================================= */
          <div
            role="tree"
            aria-label="ساختار درختی خطوط تولید و ماشین‌آلات"
            style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}
          >
            {filteredLines.map((line) => {
              const lineStations = groupedWorkCenters[line.id] || [];
              const isLineCollapsed = !!collapsedLines[line.id];
              const lineMachinesCount = lineStations.reduce((acc, wc) => acc + (wc.machines?.length || 0), 0);

              return (
                <div
                  key={line.id}
                  role="treeitem"
                  aria-expanded={!isLineCollapsed}
                  data-testid={`tree-line-${line.id}`}
                  className="brand-card-extand"
                  style={{
                    padding: "1.25rem",
                    border: selectedLineId === line.id ? "2px solid #007270" : "1px solid var(--color-border, #E4DCC8)",
                  }}
                >
                  {/* Root Node Header: Production Line */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "0.75rem",
                      cursor: "pointer",
                      paddingBottom: isLineCollapsed ? "0" : "1rem",
                      borderBottom: isLineCollapsed ? "none" : "1px solid var(--color-border, #E4DCC8)",
                    }}
                    onClick={() => toggleLineCollapse(line.id)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <button
                        type="button"
                        aria-label={isLineCollapsed ? "باز کردن شاخه خط" : "بستن شاخه خط"}
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "8px",
                          border: "1px solid var(--color-border, #E4DCC8)",
                          background: "var(--color-surface, #F5F1E6)",
                          color: "#007270",
                          fontWeight: 900,
                          fontSize: "0.9rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        {isLineCollapsed ? "▶" : "▼"}
                      </button>

                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "10px",
                          background: "#007270",
                          color: "#FFFFFF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 900,
                          fontSize: "1.1rem",
                        }}
                      >
                        🏭
                      </div>

                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "var(--color-text-main, #2B2B2B)" }}>
                            {line.name}
                          </h3>
                          <span style={{ padding: "2px 8px", borderRadius: "6px", fontSize: "0.75rem", fontFamily: "monospace", fontWeight: 700, background: "rgba(0, 114, 112, 0.1)", color: "#007270", direction: "ltr" }}>
                            {line.code}
                          </span>
                          <span style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "0.75rem", background: "rgba(16, 185, 129, 0.15)", color: "#10B981", fontWeight: 700 }}>
                            ● فعال
                          </span>
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#666", marginTop: "2px" }}>
                          سلسله‌مراتب فیزیکی خط: {lineStations.length} ایستگاه کاری | {lineMachinesCount} دستگاه مستقر
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => {
                          setTargetLineForWc(line.id);
                          setIsCreateWcOpen(true);
                        }}
                        style={{
                          padding: "5px 12px",
                          borderRadius: "8px",
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          border: "1px dashed #007270",
                          background: "rgba(0, 114, 112, 0.05)",
                          color: "#007270",
                          cursor: "pointer",
                        }}
                      >
                        + افزودن ایستگاه به خط
                      </button>
                    </div>
                  </div>

                  {/* Level 1 & Level 2 Tree Children (Stations and Machines) */}
                  {!isLineCollapsed && (
                    <div role="group" className="tree-line-branch" style={{ marginTop: "1rem" }}>
                      {lineStations.length === 0 ? (
                        <div style={{ padding: "1rem", color: "#888", fontStyle: "italic", fontSize: "0.85rem" }}>
                          هیچ ایستگاه کاری به این خط اختصاص داده نشده است.
                        </div>
                      ) : (
                        lineStations.map((wc, idx) => {
                          const isStationCollapsed = !!collapsedStations[wc.id];
                          const wcMachines = wc.machines || [];

                          return (
                            <div
                              key={wc.id}
                              role="treeitem"
                              aria-expanded={!isStationCollapsed}
                              data-testid={`tree-station-${wc.id}`}
                              style={{
                                background: "var(--color-surface, #F5F1E6)",
                                borderRadius: "12px",
                                border: "1px solid var(--color-border, #E4DCC8)",
                                padding: "0.85rem 1rem",
                              }}
                            >
                              {/* Station Node Bar */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "0.5rem",
                                  cursor: "pointer",
                                }}
                                onClick={() => toggleStationCollapse(wc.id)}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                  <button
                                    type="button"
                                    aria-label={isStationCollapsed ? "باز کردن دستگاه‌ها" : "بستن دستگاه‌ها"}
                                    style={{
                                      width: "22px",
                                      height: "22px",
                                      borderRadius: "6px",
                                      border: "1px solid var(--color-border, #E4DCC8)",
                                      background: "#FFFFFF",
                                      color: "#D4AF37",
                                      fontWeight: 900,
                                      fontSize: "0.75rem",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {isStationCollapsed ? "▶" : "▼"}
                                  </button>

                                  <span style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#D4AF37", color: "#FFFFFF", fontWeight: 800, fontSize: "0.75rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    #{wc.sequence_no ?? idx + 1}
                                  </span>

                                  <span style={{ fontSize: "0.95rem", fontWeight: 800, color: "var(--color-text-main, #2B2B2B)" }}>
                                    🚉 {wc.name}
                                  </span>

                                  <span style={{ fontSize: "0.75rem", fontFamily: "monospace", padding: "1px 6px", background: "rgba(0,0,0,0.05)", borderRadius: "4px", color: "#555", direction: "ltr" }}>
                                    {wc.code}
                                  </span>

                                  <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#666" }}>
                                    ({wcMachines.length} دستگاه)
                                  </span>
                                </div>

                                <div style={{ display: "flex", gap: "0.5rem" }} onClick={(e) => e.stopPropagation()}>
                                  <button
                                    onClick={() => {
                                      setSelectedWorkCenter(wc.id);
                                      setTargetWcForMachine(wc.id);
                                      setIsCreateMachineOpen(true);
                                    }}
                                    style={{
                                      padding: "4px 10px",
                                      borderRadius: "6px",
                                      fontSize: "0.75rem",
                                      fontWeight: 700,
                                      border: "1px solid #D4AF37",
                                      background: "#FFFFFF",
                                      color: "#B45309",
                                      cursor: "pointer",
                                    }}
                                  >
                                    + افزودن دستگاه
                                  </button>
                                </div>
                              </div>

                              {/* Level 2 Leaves: Machines */}
                              {!isStationCollapsed && (
                                <div role="group" className="tree-station-branch" style={{ marginTop: "0.75rem" }}>
                                  {wcMachines.length === 0 ? (
                                    <div style={{ fontSize: "0.8rem", color: "#888", fontStyle: "italic", padding: "4px 8px" }}>
                                      بدون دستگاه مستقر در این ایستگاه کاری
                                    </div>
                                  ) : (
                                    wcMachines.map((m: any) => (
                                      <div
                                        key={m.id || m.code}
                                        role="treeitem"
                                        data-testid={`tree-machine-${m.id || m.code}`}
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          background: "#FFFFFF",
                                          padding: "6px 12px",
                                          borderRadius: "8px",
                                          border: "1px solid var(--color-border, #E4DCC8)",
                                        }}
                                      >
                                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                          <span style={{ fontSize: "0.9rem" }}>⚙️</span>
                                          <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B" }}>
                                            {m.name}
                                          </span>
                                          <span style={{ fontSize: "0.75rem", fontFamily: "monospace", color: "#777", direction: "ltr" }}>
                                            ({m.code})
                                          </span>
                                        </div>

                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                          <span style={{ fontSize: "0.75rem", fontWeight: 700, color: m.is_active ? "#10B981" : "#9CA3AF" }}>
                                            {m.is_active ? "● فعال" : "○ غیرفعال"}
                                          </span>
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* =========================================================================
             PIPELINE FLOW DIAGRAM VIEW
             Horizontal Layout with Flow Connectors & Station Cards
             ========================================================================= */
          filteredLines.map((line) => {
            const lineStations = groupedWorkCenters[line.id] || [];

            return (
              <div
                key={line.id}
                className="brand-card-extand"
                style={{
                  padding: "1.5rem",
                  border: selectedLineId === line.id ? "2px solid #007270" : "1px solid var(--color-border, #E4DCC8)",
                }}
              >
                {/* Line Header Container */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem", paddingBottom: "1rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    <div style={{ width: "42px", height: "42px", borderRadius: "12px", background: "#007270", color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: "1.2rem" }}>
                      🏭
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <h2 style={{ margin: 0, fontSize: "1.2rem", fontWeight: 800, color: "var(--color-text-main, #2B2B2B)" }}>{line.name}</h2>
                        <span style={{ padding: "2px 8px", borderRadius: "6px", fontSize: "0.75rem", fontFamily: "monospace", fontWeight: 700, background: "rgba(0, 114, 112, 0.1)", color: "#007270", border: "1px solid rgba(0, 114, 112, 0.2)", direction: "ltr" }}>
                          {line.code}
                        </span>
                        <span style={{ padding: "2px 8px", borderRadius: "12px", fontSize: "0.75rem", background: "rgba(16, 185, 129, 0.15)", color: "#10B981", display: "flex", alignItems: "center", gap: "4px", fontWeight: 700 }}>
                          ● فعال
                        </span>
                      </div>
                      <p style={{ margin: "4px 0 0 0", fontSize: "0.85rem", color: "#666" }}>
                        تعداد {lineStations.length} ایستگاه کاری متصل به این خط تولید
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => setSelectedLine(selectedLineId === line.id ? null : line.id)}
                    style={{
                      padding: "6px 14px",
                      borderRadius: "8px",
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      border: "1px solid #007270",
                      background: selectedLineId === line.id ? "#007270" : "transparent",
                      color: selectedLineId === line.id ? "#FFFFFF" : "#007270",
                      cursor: "pointer",
                    }}
                  >
                    {selectedLineId === line.id ? "انتخاب‌شده ✓" : "تصفیه این خط"}
                  </button>
                </div>

                {/* GRAPHICAL FLOWCHART PATHWAYS */}
                {lineStations.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "2rem", background: "rgba(0,0,0,0.02)", borderRadius: "12px", border: "1px dashed var(--color-border, #E4DCC8)", color: "#666", fontSize: "0.85rem" }}>
                    هیچ ایستگاهی هنوز به این خط تخصیص نیافته است.
                  </div>
                ) : (
                  <div style={{ padding: "0.5rem 0" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "1.25rem 1rem" }}>
                      {lineStations.map((wc, idx) => {
                        const isSelectedWc = selectedWorkCenterId === wc.id;

                        return (
                          <div key={wc.id} style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                            {/* Station Node Card */}
                            <div
                              onClick={() => setSelectedWorkCenter(wc.id)}
                              style={{
                                width: "260px",
                                borderRadius: "16px",
                                padding: "1rem",
                                background: isSelectedWc ? "#FFFFFF" : "var(--color-surface, #F5F1E6)",
                                border: isSelectedWc ? "2px solid #D4AF37" : "1px solid var(--color-border, #E4DCC8)",
                                boxShadow: isSelectedWc ? "0 4px 16px rgba(212, 175, 55, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.05)",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                              }}
                            >
                              {/* Station Header */}
                              <div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                                  <span style={{ width: "28px", height: "28px", borderRadius: "50%", background: "#D4AF37", color: "#FFFFFF", fontWeight: 900, fontSize: "0.8rem", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(212, 175, 55, 0.3)" }}>
                                    #{wc.sequence_no ?? idx + 1}
                                  </span>
                                  <span style={{ fontSize: "0.75rem", fontFamily: "monospace", padding: "2px 6px", background: "rgba(0,0,0,0.05)", borderRadius: "4px", color: "#555", direction: "ltr" }}>
                                    {wc.code}
                                  </span>
                                </div>

                                <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 800, color: "var(--color-text-main, #2B2B2B)", display: "flex", alignItems: "center", gap: "6px" }}>
                                  <span>🚉</span>
                                  <span>{wc.name}</span>
                                </h3>
                              </div>

                              {/* Station Machines Nested Box */}
                              <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid var(--color-border, #E4DCC8)" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "0.8rem", color: "#666", fontWeight: 600, marginBottom: "0.5rem" }}>
                                  <span>دستگاه‌های مستقر ({wc.machines?.length || 0})</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedWorkCenter(wc.id);
                                      setTargetWcForMachine(wc.id);
                                      setIsCreateMachineOpen(true);
                                    }}
                                    style={{ background: "none", border: "none", color: "#007270", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer" }}
                                  >
                                    + افزودن
                                  </button>
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", maxHeight: "100px", overflowY: "auto" }}>
                                  {wc.machines && wc.machines.length > 0 ? (
                                    wc.machines.map((m: any) => (
                                      <div
                                        key={m.id || m.code}
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          padding: "4px 8px",
                                          borderRadius: "6px",
                                          background: "#FFFFFF",
                                          border: "1px solid var(--color-border, #E4DCC8)",
                                          fontSize: "0.75rem",
                                        }}
                                      >
                                        <span style={{ fontWeight: 600, color: "#2B2B2B", display: "flex", alignItems: "center", gap: "4px" }}>
                                          <span>⚙️</span>
                                          <span>{m.name}</span>
                                        </span>
                                        <span style={{ fontSize: "0.7rem", color: "#888", fontFamily: "monospace", direction: "ltr" }}>
                                          ({m.code})
                                        </span>
                                      </div>
                                    ))
                                  ) : (
                                    <div style={{ fontSize: "0.75rem", color: "#888", fontStyle: "italic", textAlign: "center", padding: "4px" }}>
                                      بدون دستگاه
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* RTL Directional SVG Flow Arrow */}
                            {idx < lineStations.length - 1 && (
                              <div style={{ display: "flex", flexShrink: 0, alignItems: "center", justifyContent: "center" }}>
                                <svg width="32" height="24" viewBox="0 0 32 24" fill="none">
                                  <line x1="32" y1="12" x2="8" y2="12" stroke="#007270" strokeWidth="2.5" strokeDasharray="4 2" />
                                  <polygon points="10,6 0,12 10,18" fill="#007270" />
                                </svg>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* 5. UNASSIGNED STATIONS ALERT BOX */}
      {unassignedWcCount > 0 && (
        <div
          className="brand-card-extand"
          style={{
            background: "rgba(245, 158, 11, 0.08)",
            border: "1px solid rgba(245, 158, 11, 0.4)",
            padding: "1.25rem",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
            <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#D97706", display: "flex", alignItems: "center", gap: "8px" }}>
              <span>⚠️</span>
              <span>ایستگاه‌های کاری بدون خط تولید ({unassignedWcCount})</span>
            </h3>
            <span style={{ fontSize: "0.85rem", color: "#666" }}>این ایستگاه‌ها هنوز به هیچ خط تولیدی متصل نشده‌اند.</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "0.75rem" }}>
            {(groupedWorkCenters["unassigned"] || []).map((wc) => (
              <div
                key={wc.id}
                style={{
                  background: "#FFFFFF",
                  borderRadius: "12px",
                  padding: "0.75rem",
                  border: "1px solid var(--color-border, #E4DCC8)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B" }}>
                    {wc.name} <span style={{ fontSize: "0.75rem", color: "#888", direction: "ltr" }}>({wc.code})</span>
                  </div>
                </div>

                {lines.length > 0 && (
                  <select
                    aria-label={`انتساب ایستگاه ${wc.name} به خط تولید`}
                    style={{
                      padding: "4px 8px",
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      background: "var(--color-surface, #F5F1E6)",
                      color: "#2B2B2B",
                      border: "1px solid var(--color-border, #E4DCC8)",
                      outline: "none",
                    }}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      assignWorkCenter.mutate(
                        {
                          id: wc.id,
                          data: {
                            production_line: e.target.value,
                            sequence_no: 1,
                          } as any,
                        },
                        {
                          onSuccess: () => {
                            refetchWorkCenters();
                          },
                        }
                      );
                    }}
                    defaultValue=""
                  >
                    <option value="" disabled>
                      انتساب به خط...
                    </option>
                    {lines.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6. CREATE LINE MODAL */}
      {isCreateLineOpen && (
        <div
          role="dialog"
          aria-label="تعریف خط تولید جدید"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            className="brand-card-extand"
            style={{
              padding: "1.5rem",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>🏭 تعریف خط تولید جدید</h3>
              <button onClick={() => setIsCreateLineOpen(false)} style={{ background: "none", border: "none", color: "#888", fontSize: "1.2rem", cursor: "pointer" }}>
                ✕
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>کد خط (مثال: LINE-01)</label>
                <input
                  type="text"
                  placeholder="LINE-01"
                  value={newLineCode}
                  onChange={(e) => setNewLineCode(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", direction: "ltr" }}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>نام کامل خط تولید</label>
                <input
                  type="text"
                  placeholder="خط اصلی مونتاژ"
                  value={newLineName}
                  onChange={(e) => setNewLineName(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
                />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "1.25rem", paddingTop: "0.75rem", borderTop: "1px solid var(--color-border, #E4DCC8)" }}>
              <button onClick={() => setIsCreateLineOpen(false)} style={{ padding: "8px 16px", borderRadius: "10px", background: "none", border: "1px solid var(--color-border, #E4DCC8)", color: "#555", fontSize: "0.85rem", cursor: "pointer" }}>
                انصراف
              </button>
              <button onClick={handleCreateLine} disabled={createLine.isPending} style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer" }}>
                {createLine.isPending ? "در حال ثبت..." : "ذخیره خط جدید"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 7. CREATE WORK CENTER MODAL */}
      {isCreateWcOpen && (
        <div
          role="dialog"
          aria-label="ثبت ایستگاه کاری جدید"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            className="brand-card-extand"
            style={{
              padding: "1.5rem",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>🚉 ثبت ایستگاه کاری جدید</h3>
              <button onClick={() => setIsCreateWcOpen(false)} style={{ background: "none", border: "none", color: "#888", fontSize: "1.2rem", cursor: "pointer" }}>
                ✕
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>خط تولید (اختیاری)</label>
                <select
                  aria-label="انتخاب خط تولید"
                  value={targetLineForWc || selectedLineId || ""}
                  onChange={(e) => setTargetLineForWc(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
                >
                  <option value="">بدون خط تولید (تخصیص‌نیافته)</option>
                  {lines.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} ({l.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>کد ایستگاه (مثال: WC-01)</label>
                <input
                  type="text"
                  placeholder="WC-01"
                  value={newWcCode}
                  onChange={(e) => setNewWcCode(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", direction: "ltr" }}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>نام ایستگاه کاری</label>
                <input
                  type="text"
                  placeholder="ایستگاه برشکاری"
                  value={newWcName}
                  onChange={(e) => setNewWcName(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
                />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "1.25rem", paddingTop: "0.75rem", borderTop: "1px solid var(--color-border, #E4DCC8)" }}>
              <button onClick={() => setIsCreateWcOpen(false)} style={{ padding: "8px 16px", borderRadius: "10px", background: "none", border: "1px solid var(--color-border, #E4DCC8)", color: "#555", fontSize: "0.85rem", cursor: "pointer" }}>
                انصراف
              </button>
              <button onClick={handleCreateWorkCenter} disabled={createWorkCenter.isPending} style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer" }}>
                {createWorkCenter.isPending ? "در حال ثبت..." : "ذخیره ایستگاه"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 8. CREATE MACHINE MODAL */}
      {isCreateMachineOpen && (
        <div
          role="dialog"
          aria-label="ثبت دستگاه / تجهیز جدید"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            className="brand-card-extand"
            style={{
              padding: "1.5rem",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>⚙️ ثبت دستگاه / تجهیز جدید</h3>
              <button onClick={() => setIsCreateMachineOpen(false)} style={{ background: "none", border: "none", color: "#888", fontSize: "1.2rem", cursor: "pointer" }}>
                ✕
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label htmlFor="target-wc-select" style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>
                  ایستگاه کاری مقصد
                </label>
                <select
                  id="target-wc-select"
                  aria-label="ایستگاه کاری مقصد"
                  value={targetWcForMachine || selectedWorkCenterId || ""}
                  onChange={(e) => setTargetWcForMachine(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
                >
                  <option value="" disabled>یک ایستگاه کاری انتخاب کنید...</option>
                  {workCenters.map((wc) => (
                    <option key={wc.id} value={wc.id}>
                      {wc.name} ({wc.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>کد دستگاه (مثال: MC-CNC-01)</label>
                <input
                  type="text"
                  placeholder="MC-CNC-01"
                  value={newMachineCode}
                  onChange={(e) => setNewMachineCode(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", direction: "ltr" }}
                />
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>نام دستگاه</label>
                <input
                  type="text"
                  placeholder="دستگاه تراش CNC ۴ محوره"
                  value={newMachineName}
                  onChange={(e) => setNewMachineName(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
                />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "1.25rem", paddingTop: "0.75rem", borderTop: "1px solid var(--color-border, #E4DCC8)" }}>
              <button onClick={() => setIsCreateMachineOpen(false)} style={{ padding: "8px 16px", borderRadius: "10px", background: "none", border: "1px solid var(--color-border, #E4DCC8)", color: "#555", fontSize: "0.85rem", cursor: "pointer" }}>
                انصراف
              </button>
              <button onClick={handleCreateMachine} disabled={createMachine.isPending} style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: "pointer" }}>
                {createMachine.isPending ? "در حال ثبت..." : "ذخیره دستگاه"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
